import { Button } from '@daily/shared/components/Button';
import { AppsIcon } from '@daily/shared/components/Icons/AppsIcon';
import { CloseIcon } from '@daily/shared/components/Icons/CloseIcon';
import { ScreenshareIcon } from '@daily/shared/components/Icons/ScreenshareIcon';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import {
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import { useTranslation } from 'react-i18next';

import { AppIcon } from '/components/Apps/AppIcon';
import {
  CustomIntegration,
  useCustomIntegrations,
} from '/hooks/useCustomIntegrations';
import { useScreens } from '/hooks/useScreens';

const btnStyle = {
  background: 'var(--badge-default-bg)',
  borderRadius: 4,
};

export const AppsStatus: React.FC = () => {
  const { t } = useTranslation();
  const screens = useScreens();
  const { runningMainIntegrations, stopIntegration } = useCustomIntegrations();
  const localSessionId = useLocalSessionId();

  const screenIds = screens.map((s) => s.session_id);
  const firstAppUserName = useParticipantProperty(
    runningMainIntegrations?.[0]?.startedBy,
    'user_name'
  );

  const handleClickStop = (app: CustomIntegration) => {
    stopIntegration(app.id);
  };

  const isSharingApp = runningMainIntegrations.some(
    (i) => i.startedBy === localSessionId
  );
  const isSingleApp =
    screenIds.length === 0 && runningMainIntegrations.length === 1;
  let message: string;
  let actionOrIcon: JSX.Element;

  if (isSharingApp) {
    const app = runningMainIntegrations.find(
      (i) => i.startedBy === localSessionId
    );
    message = t('integrations.stopSharing', {
      label: app.label,
    });
    actionOrIcon = (
      <Button
        variant="danger"
        onClick={() => handleClickStop(app)}
        style={{
          borderRadius: btnStyle.borderRadius,
          padding: 3,
        }}
      >
        <CloseIcon variant="default" size={16} />
      </Button>
    );
  } else if (isSingleApp) {
    const app = runningMainIntegrations?.[0];
    if (app.shared) {
      message = t('integrations.isSharing', {
        name: firstAppUserName ?? t('people.guest'),
        label: app.label,
      });
    } else {
      message = t('integrations.onlyYouCanSee', {
        label: app.label,
      });
    }
    actionOrIcon = <AppIcon id={app.id} size={24} style={btnStyle} />;
  } else if (screenIds.length) {
    const count = runningMainIntegrations.length + screenIds.length;
    message = t('integrations.itemsAreShared', {
      count,
    });
    actionOrIcon = <ScreenshareIcon size={24} style={btnStyle} />;
  } else {
    const count = runningMainIntegrations.length;
    message = t('integrations.appsAreShared', {
      count,
    });
    actionOrIcon = <AppsIcon size={24} style={btnStyle} />;
  }

  return (
    <Stack align="center" gap={8} horizontal>
      <Text>{message}</Text>
      {actionOrIcon}
    </Stack>
  );
};
